// ============================================================================
// OMS PO list API — self-contained axios calls. Uses the app's default axios
// instance (baseURL + JWT set globally in src/index.tsx), so relative paths
// inherit auth. Thin: the backend owns the tab split and the search.
// ============================================================================
import axios from "axios";
import { errorActions } from "../../../helpers/auth_helper";
import { DeliveryStatus, DocPdfRef } from "../types";

// One flat PO/allotment row — an allocation with just enough order context to scan and click through.
export interface PoRow {
  id: number; // allocation id
  po_number: string | null;
  state: string; // derived OMS fulfilment status: planned / in_progress / completed / cancelled / error / held
  // The live ERROR overlay across this allotment's legs (shown on hover of the status tag), its
  // kind (`invoice` = a payment wait vs `generic` = a real failure), and the HELD reason.
  error_message: string | null;
  error_kind: string | null;
  hold_reason: string | null;
  delivery_status: DeliveryStatus | null; // delivery axis, independent of the OMS/fulfilment status
  shipment_type: string | null;
  supplier_name: string | null;
  sales_order_id: number | null; // click-through target → /oms/:id
  lead_id: string | null;
  zoho_salesorder_number: string | null;
  client_name: string | null;
  is_express: boolean;
  is_unloading: boolean; // order needs unloading labour at the customer site (like is_express)
  item_count: number;
  // This allotment's own PO / delivery-challan PDFs — the documents an operator is asked for by PO
  // number, so they hang off the row rather than behind a click into the order. Empty until pushed.
  documents?: DocPdfRef[];
  target_delivery_date: string | null;
  expected_delivery_date: string | null;
  created_at: string | null;
}

export interface PoListResponse {
  count: number;
  limit: number;
  offset: number;
  results: PoRow[];
  tab: PoTab;
  // Both tabs' totals for the current search, computed server-side in ONE query.
  tab_counts: PoTabCounts;
}

// The board's two cuts. Both are SQL predicates server-side, so switching tabs paginates in the
// database instead of folding a derived state over every allocation.
export type PoTab = "pending_delivery" | "delayed" | "due_today" | "all";

export interface PoTabCounts {
  pending_delivery: number;
  delayed: number;
  due_today: number;
  all: number;
}

export interface ListPoParams {
  tab?: PoTab;
  // Scope to one FULFILMENT warehouse — the MD WH the last leg ships from, i.e. the team that owes
  // the customer this delivery. Applies to every tab and to a search.
  warehouse_id?: number;
  search?: string;
  limit?: number;
  offset?: number;
}

export async function listPurchaseOrders(
  params: ListPoParams = {}
): Promise<PoListResponse | undefined> {
  try {
    return await axios
      .get("/oms/purchase-orders/", { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading purchase orders");
        return undefined;
      });
  } catch {
    return undefined;
  }
}
